import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Card,
	CardBody,
	Row,
	Col,
	Spinner,
	Alert,
	Badge,
} from "reactstrap";
import { getAccountBalance, getIncomeVerification, createShareableBankLink } from "api/PlaidAPI";
import { sendTwilioMessage } from "api/TwilioApi";

/**
 * Modal component to display Plaid account details including balance and income verification
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.toggle - Function to toggle modal visibility
 * @param {string} props.dcDealerID - DealerClick dealer ID (8-digit format)
 * @param {string} props.dcContactID - DealerClick contact ID
 * @param {string} props.contactName - Name of the contact for display
 * @param {string} props.contactPhone - Phone number of the contact
 */
const PlaidDetailsModal = ({ isOpen, toggle, dcDealerID, dcContactID, contactName, contactPhone }) => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [balanceData, setBalanceData] = useState(null);
	const [incomeData, setIncomeData] = useState(null);

	// Bank link sending state
	const [bankLinkSending, setBankLinkSending] = useState(false);
	const [bankLinkSuccess, setBankLinkSuccess] = useState(false);

	/**
	 * Fetches balance and income data from Plaid API
	 */
	const fetchPlaidDetails = useCallback(async () => {
		setLoading(true);
		setError(null);

		try {
			// Fetch both balance and income data in parallel
			const [balanceResponse, incomeResponse] = await Promise.allSettled([
				getAccountBalance(dcDealerID, dcContactID),
				getIncomeVerification(dcDealerID, dcContactID),
			]);

			// Process balance data
			if (balanceResponse.status === "fulfilled" && balanceResponse.value?.data?.success) {
				setBalanceData(balanceResponse.value.data);
			}

			// Process income data
			if (incomeResponse.status === "fulfilled" && incomeResponse.value?.data?.success) {
				setIncomeData(incomeResponse.value.data);
			}

			// If both failed, show error
			if (balanceResponse.status === "rejected" && incomeResponse.status === "rejected") {
				setError("Unable to load Plaid account details. Please try again later.");
			}
		} catch (err) {
			console.error("Error fetching Plaid details:", err);
			setError("An error occurred while loading account details.");
		} finally {
			setLoading(false);
		}
	}, [dcDealerID, dcContactID]);

	useEffect(() => {
		if (isOpen) {
			fetchPlaidDetails();
			// Reset bank link success state when modal opens
			setBankLinkSuccess(false);
		}
	}, [isOpen, fetchPlaidDetails]);

	/**
	 * Handles sending bank linking URL to customer via SMS
	 */
	const handleSendBankLink = async () => {
		console.log("=== PlaidDetailsModal handleSendBankLink called ===");
		console.log("dcDealerID:", dcDealerID);
		console.log("dcContactID:", dcContactID);
		console.log("contactName:", contactName);

		setBankLinkSending(true);
		setBankLinkSuccess(false);

		try {
			// Create shareable bank link
			console.log("Calling createShareableBankLink...");
			const response = await createShareableBankLink(dcDealerID, dcContactID);
			console.log("createShareableBankLink response:", response);

			if (response.data?.shareable_url) {
				console.log("Got shareable_url:", response.data.shareable_url);

				// Get user data and customer contact info from sessionStorage
				const user = JSON.parse(sessionStorage.getItem("user"));
				console.log("User:", user);
				const dealerNumber = user?.messagingNumber;
				console.log("Dealer messagingNumber:", dealerNumber);

				if (!dealerNumber) {
					alert("Dealer messaging number not configured. Please contact support.");
					return;
				}

				// Get first name from contact name
				const firstName = contactName.split(" ")[0] || contactName;
				console.log("firstName:", firstName);

				// Use phone number passed as prop
				console.log("contactPhone prop:", contactPhone);
				let phoneNumber = contactPhone || "";

				if (!phoneNumber) {
					console.error("No phone number provided!");
					alert("No phone number available for customer. Please add a phone number and try again.");
					return;
				}

				// Remove non-digits from phone number
				phoneNumber = phoneNumber.replace(/\D/g, "");
				console.log("phoneNumber after strip:", phoneNumber);

				if (!phoneNumber) {
					console.error("Phone number is empty after removing non-digits!");
					alert("Invalid phone number format. Please update the customer's phone number.");
					return;
				}

				// Send SMS to customer
				const message = {
					fromNumber: "+1" + dealerNumber,
					recipients: ["+1" + phoneNumber],
					messageBody: `Hi ${firstName}, link your bank account to continue: ${response.data.shareable_url}`,
					customerID: dcContactID,
					personType: "Customer",
					locationID: user?.locationID,
				};

				await sendTwilioMessage(message);
				setBankLinkSuccess(true);
			}
		} catch (error) {
			console.error("Error sending bank link:", error);
			alert(
				error.response?.data?.message ||
					"Failed to send bank link. Please try again."
			);
		} finally {
			setBankLinkSending(false);
		}
	};

	/**
	 * Formats currency values
	 */
	const formatCurrency = (amount) => {
		if (amount === null || amount === undefined) return "N/A";
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
		}).format(amount);
	};

	/**
	 * Formats date strings
	 */
	const formatDate = (dateString) => {
		if (!dateString) return "N/A";
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	/**
	 * Renders balance information card
	 */
	const renderBalanceCard = () => {
		if (!balanceData) return null;

		const balanceTrend =
			balanceData.available_balance > balanceData.average_balance_30d ? "up" : "down";

		return (
			<Card className="mb-3">
				<CardBody>
					<h5 className="mb-3">Account Balance</h5>
					<Row>
						<Col md="6">
							<div className="mb-3">
								<small className="text-muted">Current Balance</small>
								<h4 className="mb-0">{formatCurrency(balanceData.available_balance)}</h4>
								<small className="text-muted">
									As of {formatDate(balanceData.balance_fetched_at)}
								</small>
							</div>
						</Col>
						<Col md="6">
							<div className="mb-2">
								<small className="text-muted">Account</small>
								<div>{balanceData.account_name || "N/A"}</div>
							</div>
						</Col>
					</Row>
					<hr />
					<Row>
						<Col md="4">
							<div>
								<small className="text-muted">30-Day Average</small>
								<div className="font-weight-bold">
									{formatCurrency(balanceData.average_balance_30d)}
								</div>
							</div>
						</Col>
						<Col md="4">
							<div>
								<small className="text-muted">90-Day Average</small>
								<div className="font-weight-bold">
									{formatCurrency(balanceData.average_balance_90d)}
								</div>
							</div>
						</Col>
						<Col md="4">
							<div>
								<small className="text-muted">Trend</small>
								<div>
									<Badge color={balanceTrend === "up" ? "success" : "warning"}>
										{balanceTrend === "up" ? "Trending Up 📈" : "Trending Down 📉"}
									</Badge>
								</div>
							</div>
						</Col>
					</Row>
				</CardBody>
			</Card>
		);
	};

	/**
	 * Renders bank income information card
	 */
	const renderBankIncomeCard = () => {
		if (!incomeData?.bank_income) return null;

		const { bank_income } = incomeData;

		return (
			<Card className="mb-3">
				<CardBody>
					<h5 className="mb-3">Bank Income Verification</h5>
					<Row className="mb-3">
						<Col md="6">
							<div className="mb-2">
								<small className="text-muted">Monthly Average Income</small>
								<h4 className="mb-0">{formatCurrency(bank_income.monthly_average_income)}</h4>
							</div>
						</Col>
						<Col md="6">
							<div className="mb-2">
								<small className="text-muted">Annual Income (Last 12 Months)</small>
								<h4 className="mb-0">{formatCurrency(bank_income.total_income_last_12_months)}</h4>
							</div>
						</Col>
					</Row>
					<hr />
					<div className="mb-2">
						<small className="text-muted">
							Analyzed {bank_income.accounts_analyzed} account(s) over {bank_income.days_analyzed} days
						</small>
					</div>
					{bank_income.income_streams && bank_income.income_streams.length > 0 && (
						<>
							<h6 className="mt-3 mb-2">Income Sources</h6>
							{bank_income.income_streams.map((stream, index) => (
								<div key={index} className="mb-2 p-2 border-left border-primary" style={{ borderLeftWidth: "3px" }}>
									<div className="font-weight-bold">{stream.name}</div>
									<Row className="mt-1">
										<Col md="4">
											<small className="text-muted">Frequency</small>
											<div>{stream.frequency}</div>
										</Col>
										<Col md="4">
											<small className="text-muted">Average Amount</small>
											<div>{formatCurrency(stream.average_amount)}</div>
										</Col>
										<Col md="4">
											<small className="text-muted">Last Payment</small>
											<div>{formatCurrency(stream.last_amount)}</div>
											<small className="text-muted">{formatDate(stream.last_date)}</small>
										</Col>
									</Row>
								</div>
							))}
						</>
					)}
				</CardBody>
			</Card>
		);
	};

	/**
	 * Renders payroll income information card
	 */
	const renderPayrollIncomeCard = () => {
		if (!incomeData?.payroll_income) return null;

		const { payroll_income } = incomeData;

		return (
			<Card className="mb-3">
				<CardBody>
					<h5 className="mb-3">Payroll Income Verification</h5>
					<Row className="mb-3">
						<Col md="6">
							<div className="mb-2">
								<small className="text-muted">Monthly Income</small>
								<h4 className="mb-0">{formatCurrency(payroll_income.monthly_income)}</h4>
							</div>
						</Col>
						<Col md="6">
							<div className="mb-2">
								<small className="text-muted">Annual Income</small>
								<h4 className="mb-0">{formatCurrency(payroll_income.annual_income)}</h4>
							</div>
						</Col>
					</Row>
					<hr />
					<Row className="mb-3">
						<Col md="6">
							<div className="mb-2">
								<small className="text-muted">Employer</small>
								<div className="font-weight-bold">{payroll_income.employer?.name || "N/A"}</div>
							</div>
						</Col>
						<Col md="6">
							<div className="mb-2">
								<small className="text-muted">Pay Frequency</small>
								<div>{payroll_income.pay_frequency || "N/A"}</div>
							</div>
						</Col>
					</Row>
					<Row>
						<Col md="6">
							<div className="mb-2">
								<small className="text-muted">Last Pay Amount</small>
								<div>{formatCurrency(payroll_income.last_pay_amount)}</div>
							</div>
						</Col>
						<Col md="6">
							<div className="mb-2">
								<small className="text-muted">Last Pay Date</small>
								<div>{formatDate(payroll_income.last_pay_date)}</div>
							</div>
						</Col>
					</Row>
					<Row className="mt-2">
						<Col md="12">
							<div className="mb-2">
								<small className="text-muted">Year-to-Date Gross Income</small>
								<div className="font-weight-bold">{formatCurrency(payroll_income.ytd_gross_income)}</div>
							</div>
						</Col>
					</Row>
					{incomeData.risk_signals && (
						<>
							<hr />
							<div className="mt-2">
								<small className="text-muted">Risk Assessment</small>
								<div>
									<Badge color={incomeData.risk_signals.potential_fraud ? "danger" : "success"}>
										{incomeData.risk_signals.potential_fraud ? "Potential Fraud Detected" : "No Fraud Detected"}
									</Badge>
									{" "}
									<Badge color={
										incomeData.risk_signals.confidence_level === "high" ? "success" :
										incomeData.risk_signals.confidence_level === "medium" ? "warning" : "danger"
									}>
										{incomeData.risk_signals.confidence_level.toUpperCase()} Confidence
									</Badge>
								</div>
							</div>
						</>
					)}
				</CardBody>
			</Card>
		);
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="lg">
			<ModalHeader toggle={toggle}>
				Plaid Account Details - {contactName}
			</ModalHeader>
			<ModalBody>
				{loading && (
					<div className="text-center py-5">
						<Spinner color="primary" />
						<p className="mt-2">Loading account details...</p>
					</div>
				)}

				{error && (
					<Alert color="danger">
						{error}
					</Alert>
				)}

				{!loading && !error && !balanceData && !incomeData && (
					<Alert color="info">
						No Plaid account details available for this contact.
					</Alert>
				)}

				{!loading && (
					<>
						{renderBalanceCard()}
						{renderBankIncomeCard()}
						{renderPayrollIncomeCard()}
					</>
				)}
			</ModalBody>
			<ModalFooter>
				<div className="d-flex justify-content-between w-100 align-items-center">
					<div>
						{bankLinkSuccess && (
							<small className="text-success">
								✓ Bank link sent successfully!
							</small>
						)}
					</div>
					<div>
						<Button
							color="primary"
							onClick={handleSendBankLink}
							disabled={bankLinkSending}
							className="mr-2"
						>
							{bankLinkSending ? "Sending..." : bankLinkSuccess ? "Resend Bank Link" : "Send Bank Link"}
						</Button>
						<Button color="secondary" onClick={toggle}>
							Close
						</Button>
					</div>
				</div>
			</ModalFooter>
		</Modal>
	);
};

PlaidDetailsModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	toggle: PropTypes.func.isRequired,
	dcDealerID: PropTypes.string.isRequired,
	dcContactID: PropTypes.string.isRequired,
	contactName: PropTypes.string.isRequired,
	contactPhone: PropTypes.string,
};

export default PlaidDetailsModal;
